import React, { useState, useEffect } from 'react';
import StorageManager from '../services/StorageManager';
import VideoModal from '../components/VideoModal';
import TopPicksSelector from '../components/TopPicksSelector';
import PageFooter from '../components/PageFooter';
import { FlameIcon, AnimatedFlameIcon } from '../components/Icons';
import LoginModal from './LoginModal';

function BrowsePage({ currentUser, onLogout, onSelectPilot, onLogin, onNavigate }) {
  const [allPilots, setAllPilots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loginModalMode, setLoginModalMode] = useState(null);
  const [selectedPilotModal, setSelectedPilotModal] = useState(null);
  const [pilotSelectionSource, setPilotSelectionSource] = useState(null);
  const [viewedPilotsSession, setViewedPilotsSession] = useState(new Set());
  const [showVoterGuide, setShowVoterGuide] = useState(false);
  const [showMemberAgreement, setShowMemberAgreement] = useState(false);
  const [votedPilotIds, setVotedPilotIds] = useState(new Set());
  const [hoveredNav, setHoveredNav] = useState(null);
  const [showTopPicks, setShowTopPicks] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [activeGenre, setActiveGenre] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [showGenreSelection, setShowGenreSelection] = useState(false);

  // Get curated pilots for a genre — pilots with a seasonSlot value, sorted by slot order
  const getCuratedPilots = (genre) => {
    return allPilots
      .filter(p => p.genre === genre && p.seasonSlot)
      .sort((a, b) => a.seasonSlot - b.seasonSlot);
  };

  // Load pilots
  useEffect(() => {
    const loadPilots = async () => {
      try {
        const pilots = await StorageManager.getPilotsForVoting();
        setAllPilots(pilots || []);
      } catch (err) {
        console.error('Error loading pilots:', err);
      }
      setLoading(false);
    };
    loadPilots();
  }, []);

  // Load user's voted pilots
  useEffect(() => {
    if (currentUser?.id) {
      StorageManager.getVoterVotes(currentUser.id).then(votes => {
        const pilotIds = new Set(votes.map(v =>
          Array.isArray(v.pilotId) ? v.pilotId[0] : v.pilotId
        ));
        setVotedPilotIds(pilotIds);
      }).catch(err => console.error('Error loading voted pilots:', err));
    } else {
      setVotedPilotIds(new Set());
    }
  }, [currentUser?.id]);

  // Select a pilot and track it
  const selectPilot = (pilot, source) => {
    if (pilot) {
      setSelectedPilotModal(pilot);
      setPilotSelectionSource(source);
      setViewedPilotsSession(prev => new Set([...prev, pilot.id]));
    }
  };

  // Get the active genre's pilot list
  const getActiveGenrePilots = () => {
    if (!activeGenre) return allPilots;
    return getCuratedPilots(activeGenre);
  };

  // Get next pilot sequentially within the active genre
  const getNextPilot = () => {
    const genrePilots = getActiveGenrePilots();
    if (!selectedPilotModal || genrePilots.length === 0) return { pilot: null, allViewed: true };

    const currentIndex = genrePilots.findIndex(p => p.id === selectedPilotModal.id);
    const nextIndex = currentIndex + 1;

    if (nextIndex < genrePilots.length) {
      return { pilot: genrePilots[nextIndex], source: 'season' };
    }
    return { pilot: null, allViewed: true };
  };

  // Get previous pilot within the active genre
  const getPreviousPilot = () => {
    const genrePilots = getActiveGenrePilots();
    if (!selectedPilotModal || genrePilots.length === 0) return null;
    const currentIndex = genrePilots.findIndex(p => p.id === selectedPilotModal.id);
    if (currentIndex > 0) return genrePilots[currentIndex - 1];
    return null;
  };

  // Start viewing a genre's season
  const handleViewGenre = (genre) => {
    setActiveGenre(genre);
    const genrePilots = getCuratedPilots(genre);
    const hasNotVotedOn = (pilotId) => !votedPilotIds.has(pilotId);
    const unvotedPilots = genrePilots.filter(p => hasNotVotedOn(p.id));
    if (unvotedPilots.length > 0) {
      setShowWelcome(true);
      selectPilot(unvotedPilots[0], 'season');
    } else if (genrePilots.length > 0) {
      setShowWelcome(true);
      selectPilot(genrePilots[0], 'season');
    }
  };

  const navLinkStyle = (id) => ({
    background: 'none', border: 'none', cursor: 'pointer',
    color: hoveredNav === id ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
    fontSize: '0.9rem', fontWeight: 400, fontFamily: "'Cormorant Garamond', Georgia, serif",
    transition: 'color 0.25s ease', letterSpacing: '0.05em', padding: '0.3rem 0'
  });

  const ghostBtnStyle = (hovered, disabled) => ({
    background: 'none', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', padding: '0.8rem 2.5rem',
    color: hovered ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
    fontSize: '1rem', fontWeight: 400, fontFamily: "'Cormorant Garamond', Georgia, serif",
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.25s ease',
    letterSpacing: '0.08em',
    borderColor: hovered ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
    opacity: disabled ? 0.6 : 1
  });

  const comedyPilots = getCuratedPilots('Comedy');
  const dramaPilots = getCuratedPilots('Drama');
  const activeGenrePilots = getActiveGenrePilots();
  const totalPilots = activeGenrePilots.length;
  const unvotedCount = activeGenrePilots.filter(p => !votedPilotIds.has(p.id)).length;

  const getGenreVotedCount = (genre) => {
    const pilots = getCuratedPilots(genre);
    return pilots.filter(p => votedPilotIds.has(p.id)).length;
  };

  // Show Top Picks selector when all pilots viewed
  if (showTopPicks && allPilots.length > 0) {
    return (
      <TopPicksSelector
        currentUser={currentUser}
        allPilots={allPilots}
        onComplete={() => setShowTopPicks(false)}
        onGoHome={() => setShowTopPicks(false)}
        onBackToPilots={(pilot) => {
          setShowTopPicks(false);
          if (pilot) selectPilot(pilot, 'season');
        }}
      />
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#000000', fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#fff' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap');
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      ` }} />

      {/* Header */}
      <header style={{ padding: '1rem clamp(1.25rem, 5vw, 2rem)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.07)', position: 'relative', zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
          <span style={{ fontSize: 'clamp(0.8rem, 2.5vw, 1rem)', fontWeight: 500, letterSpacing: '0.25em', textTransform: 'uppercase', background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Pilot Light</span>
        </div>
        <nav style={{ display: 'flex', alignItems: 'center', gap: 'clamp(0.75rem, 3vw, 1.5rem)' }}>
          <button onClick={() => setShowVoterGuide(true)} style={navLinkStyle('guide')}
            onMouseEnter={() => setHoveredNav('guide')} onMouseLeave={() => setHoveredNav(null)}>Guide</button>
          <button onClick={() => setShowMemberAgreement(true)} style={navLinkStyle('agreement')}
            onMouseEnter={() => setHoveredNav('agreement')} onMouseLeave={() => setHoveredNav(null)}>Agreement</button>
          <button onClick={() => onNavigate('account')} style={navLinkStyle('account')}
            onMouseEnter={() => setHoveredNav('account')} onMouseLeave={() => setHoveredNav(null)}>Account</button>
        </nav>
      </header>

      {/* Main content — centered */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>

        {loading ? null : !showGenreSelection ? (
          <>
            {/* Phase 1: View Season button */}
            <div style={{ marginBottom: '1.5rem', animation: 'fadeInUp 0.6s ease-out' }}>
              <svg width="36" height="36" viewBox="0 0 24 24" style={{ opacity: 0.8 }}>
                <defs>
                  <linearGradient id="browseFlameGrad" x1="0%" y1="100%" x2="0%" y2="0%">
                    <stop offset="0%" stopColor="#ff6b6b" />
                    <stop offset="50%" stopColor="#feca57" />
                    <stop offset="100%" stopColor="#fff5cc" />
                  </linearGradient>
                </defs>
                <path fill="none" stroke="url(#browseFlameGrad)" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
                  d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
              </svg>
            </div>

            <h2 style={{ fontSize: '1.3rem', fontWeight: 300, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.6)',
              marginBottom: '2.5rem', animation: 'fadeInUp 0.6s ease-out 0.1s both' }}>The Season</h2>

            <button
              onClick={() => setShowGenreSelection(true)}
              onMouseEnter={() => setHoveredCard('viewSeason')}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                background: 'none',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '12px',
                padding: '1rem 3rem',
                color: hoveredCard === 'viewSeason' ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
                fontSize: '1.1rem',
                fontWeight: 400,
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                letterSpacing: '0.1em',
                borderColor: hoveredCard === 'viewSeason' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.12)',
                animation: 'fadeInUp 0.6s ease-out 0.2s both'
              }}
            >View Season</button>
          </>
        ) : (
          <>
            {/* Phase 2: Genre selection */}
            <button
              onClick={() => setShowGenreSelection(false)}
              onMouseEnter={() => setHoveredCard('back')}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: hoveredCard === 'back' ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.25)',
                fontSize: '0.82rem', fontFamily: "'Cormorant Garamond', Georgia, serif",
                marginBottom: '2rem', transition: 'color 0.25s ease', letterSpacing: '0.06em',
                animation: 'fadeInUp 0.5s ease-out'
              }}
            >← Back</button>

            <h2 style={{ fontSize: '1.3rem', fontWeight: 300, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.6)',
              marginBottom: '0.6rem', animation: 'fadeInUp 0.5s ease-out 0.05s both' }}>Choose a Genre</h2>
            <p style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem', marginBottom: '2.5rem',
              animation: 'fadeInUp 0.5s ease-out 0.1s both', letterSpacing: '0.04em' }}>
              View all pilots in the slate
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'clamp(1.5rem, 4vw, 3rem)',
              width: '100%', maxWidth: '520px', animation: 'fadeInUp 0.5s ease-out 0.15s both' }}>

              {/* Comedy card */}
              <button
                onClick={() => handleViewGenre('Comedy')}
                onMouseEnter={() => setHoveredCard('comedy')}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  background: 'none', border: '1px solid rgba(254,202,87,0.15)', borderRadius: '14px',
                  padding: 'clamp(2rem, 4vw, 3rem) clamp(1rem, 2vw, 2rem)', cursor: 'pointer',
                  transition: 'all 0.35s ease', textAlign: 'center',
                  borderColor: hoveredCard === 'comedy' ? 'rgba(254,202,87,0.4)' : 'rgba(254,202,87,0.15)',
                  boxShadow: hoveredCard === 'comedy' ? '0 0 40px rgba(254,202,87,0.08)' : 'none',
                  transform: hoveredCard === 'comedy' ? 'translateY(-2px)' : 'none'
                }}
              >
                <h3 style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', fontWeight: 400, letterSpacing: '0.1em',
                  color: '#feca57', margin: '0 0 0.6rem', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Comedy</h3>
                <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.75rem', letterSpacing: '0.06em' }}>
                  {comedyPilots.length > 0
                    ? `${getGenreVotedCount('Comedy')} of ${comedyPilots.length} reviewed`
                    : '5 pilots'}
                </span>
              </button>

              {/* Drama card */}
              <button
                onClick={() => handleViewGenre('Drama')}
                onMouseEnter={() => setHoveredCard('drama')}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  background: 'none', border: '1px solid rgba(78,205,196,0.15)', borderRadius: '14px',
                  padding: 'clamp(2rem, 4vw, 3rem) clamp(1rem, 2vw, 2rem)', cursor: 'pointer',
                  transition: 'all 0.35s ease', textAlign: 'center',
                  borderColor: hoveredCard === 'drama' ? 'rgba(78,205,196,0.4)' : 'rgba(78,205,196,0.15)',
                  boxShadow: hoveredCard === 'drama' ? '0 0 40px rgba(78,205,196,0.08)' : 'none',
                  transform: hoveredCard === 'drama' ? 'translateY(-2px)' : 'none'
                }}
              >
                <h3 style={{ fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', fontWeight: 400, letterSpacing: '0.1em',
                  color: '#4ecdc4', margin: '0 0 0.6rem', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Drama</h3>
                <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.75rem', letterSpacing: '0.06em' }}>
                  {dramaPilots.length > 0
                    ? `${getGenreVotedCount('Drama')} of ${dramaPilots.length} reviewed`
                    : '5 pilots'}
                </span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Minimal footer */}
      <footer style={{ padding: '2.5rem 2rem', textAlign: 'center', marginTop: 'auto' }}>
        <p style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.75rem', margin: 0, letterSpacing: '0.04em' }}>
          © 2026 Pilot Light. All rights reserved.
        </p>
      </footer>

      {/* VideoModal overlay */}
      {selectedPilotModal && (
        <VideoModal
          pilot={selectedPilotModal}
          currentUser={currentUser}
          onClose={() => { setSelectedPilotModal(null); setShowWelcome(false); }}
          onLogin={(user) => onLogin(user)}
          onNavigate={onNavigate}
          onWatchAnother={() => {
            const { pilot, allViewed } = getNextPilot();
            if (allViewed) {
              setSelectedPilotModal(null);
              setShowTopPicks(true);
              setShowWelcome(false);
              return 'all_viewed';
            }
            selectPilot(pilot, 'season');
            return pilot;
          }}
          onPreviousPilot={() => {
            const prev = getPreviousPilot();
            if (prev) selectPilot(prev, 'season');
          }}
          onGoHome={() => { setSelectedPilotModal(null); setShowWelcome(false); }}
          onVoteSubmit={(pilotId) => {
            setVotedPilotIds(prev => new Set([...prev, pilotId]));
          }}
          totalPilots={totalPilots}
          votedCount={totalPilots - unvotedCount}
          showWelcome={showWelcome}
          pilotNumber={selectedPilotModal ? activeGenrePilots.findIndex(p => p.id === selectedPilotModal.id) + 1 : 1}
        />
      )}

      {/* Login Modal */}
      {loginModalMode && (
        <LoginModal
          mode={loginModalMode}
          onClose={() => setLoginModalMode(null)}
          onLogin={(user) => { setLoginModalMode(null); onLogin(user); }}
          onSwitchMode={(mode) => setLoginModalMode(mode)}
        />
      )}

      {/* Voter Guide Modal */}
      {showVoterGuide && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setShowVoterGuide(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#000000', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px',
            maxWidth: '520px', width: '100%', padding: '1.8rem', maxHeight: '85vh', overflowY: 'auto',
            boxSizing: 'border-box', fontFamily: "'Cormorant Garamond', Georgia, serif"
          }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, marginBottom: '1.5rem', letterSpacing: '0.04em',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Screening Guide</h2>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.92rem', lineHeight: 1.8, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>View Season</strong> — Choose Comedy or Drama, then view all pilots in the slate.</p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Rating</strong> — An overall score vote out of 5. <span style={{ color: 'rgba(255,255,255,0.35)' }}>(Optional)</span></p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Curiosity Score</strong> — Are you interested in what comes next? <span style={{ color: 'rgba(255,255,255,0.35)' }}>(Optional)</span></p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Series Potential</strong> — Would this make a great series? <span style={{ color: 'rgba(255,255,255,0.35)' }}>(Optional)</span></p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Pull Factors</strong> — What pulled you in? What held you back? <span style={{ color: 'rgba(255,255,255,0.35)' }}>(Optional)</span></p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Comment</strong> — Anonymously or unanonymously send feedback directly to the creator. <span style={{ color: 'rgba(255,255,255,0.35)' }}>(Optional)</span></p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Top 3</strong> — After viewing all pilots, select your 3 favorites. <span style={{ color: 'rgba(255,255,255,0.35)' }}>(Optional)</span></p>
              <p style={{ marginBottom: '1rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>Rewatch</strong> — You can rewatch any pilot and update your vote at any time.</p>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', lineHeight: 1.6 }}>Your votes are anonymous. Your identity can only be revealed when submitting comments if you choose.</p>
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', marginTop: '0.8rem', fontStyle: 'italic' }}>Thank you for your time and consideration.</p>
            </div>
            <button onClick={() => setShowVoterGuide(false)} style={{
              marginTop: '1.5rem', background: 'none', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '10px', padding: '0.6rem 2rem', color: 'rgba(255,255,255,0.45)',
              fontSize: '0.88rem', fontFamily: "'Cormorant Garamond', Georgia, serif", cursor: 'pointer',
              transition: 'all 0.25s ease', letterSpacing: '0.04em'
            }}
              onMouseOver={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              onMouseOut={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
            >Close</button>
          </div>
        </div>
      )}

      {/* Member Agreement Modal */}
      {showMemberAgreement && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={() => setShowMemberAgreement(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#000000', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '16px',
            maxWidth: '520px', width: '100%', padding: '1.8rem', maxHeight: '85vh', overflowY: 'auto',
            boxSizing: 'border-box', fontFamily: "'Cormorant Garamond', Georgia, serif"
          }}>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 600, marginBottom: '1.5rem', letterSpacing: '0.04em',
              background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Member Agreement</h2>
            <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.95rem', lineHeight: 1.8 }}>
              <p style={{ marginBottom: '0.6rem', color: 'rgba(255,255,255,0.35)', fontSize: '0.82rem', fontStyle: 'italic' }}>This is a summary of key terms. The full Non-Disclosure Agreement must be accepted upon entering the screening room.</p>
              <p style={{ marginBottom: '1rem' }}>By using Pilot Light, you agree to the following terms:</p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>1. Confidentiality</strong> — All content on this platform is strictly confidential and may not be shared externally.</p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>2. No Recording</strong> — You may not record, screenshot, download, or redistribute any content.</p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>3. Intellectual Property</strong> — All pilot content remains the sole property of its creators.</p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>4. Honest Feedback</strong> — You commit to providing constructive, good-faith feedback.</p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>5. No External Contact</strong> — Do not contact creators outside this platform unless invited.</p>
              <p style={{ marginBottom: '0.8rem' }}><strong style={{ color: 'rgba(255,255,255,0.85)' }}>6. Violations</strong> — Breach of these terms may result in removal and legal action.</p>
            </div>
            <button onClick={() => setShowMemberAgreement(false)} style={{
              marginTop: '1.5rem', background: 'none', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '10px', padding: '0.6rem 2rem', color: 'rgba(255,255,255,0.45)',
              fontSize: '0.88rem', fontFamily: "'Cormorant Garamond', Georgia, serif", cursor: 'pointer',
              transition: 'all 0.25s ease', letterSpacing: '0.04em'
            }}
              onMouseOver={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              onMouseOut={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
            >Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default BrowsePage;
